import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import { IntlShape, useIntl } from 'react-intl';

import { Study } from 'app/interfaces/Study';
import ApiCalls, { API } from 'app/utils/apiCalls';
import { getReport } from 'app/utils/reports/reportsGenerator';

export const FILTERED_STUDIES_LIMIT = 10;

type Dependencies = {
  intl: IntlShape;
};

export const filterStudiesQueryOptions = (
  params?: API.StudiesFilter,
  dependencies?: Dependencies
) =>
  infiniteQueryOptions({
    queryKey: params
      ? ([{ query: '/study/filter' }, params] as const)
      : ([{ query: '/study/filter' }] as const),
    initialPageParam: 1,
    getNextPageParam: (lastPage: Study[], allPages, lastPageParam, allPageParams) =>
      lastPage.length > 0 ? lastPageParam + 1 : undefined,
    getPreviousPageParam: (firstPage, allPages, firstPageParam, allPageParams) =>
      firstPageParam >= 1 ? undefined : firstPageParam - 1,

    queryFn: async ({ pageParam, queryKey }): Promise<Study[]> => {
      const [, params] = queryKey;
      const { intl } = dependencies;
      const { data } = await ApiCalls.filterStudies({
        ...params,
        page_number: pageParam,
        use_study_structure: true,
      });
      const studies = data?.studies || [];

      const formattedStudies = studies.map(({ comment, images, ...studyRest }) => ({
        images,
        ...studyRest,
        comment:
          comment ||
          getReport(
            studyRest.animalName,
            studyRest.ownerName,
            images,
            intl,
            studyRest.creationDate
          ),
      }));
      return formattedStudies;
    },
  });

export default function useFilterStudiesInfiniteQuery(
  params: API.StudiesFilter,
  options?: Omit<
    ReturnType<typeof filterStudiesQueryOptions>,
    'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam' | 'getPreviousPageParam'
  >
) {
  const intl = useIntl();
  return useInfiniteQuery({ ...options, ...filterStudiesQueryOptions(params, { intl }) });
}
